import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { AppointmentService } from '@/services/firebase/appointments';
import { DoctorAlertService } from '@/services/firebase/doctorAlert';
import { NextUpAccompanistModal } from '@/components/patients/NextUpAccompanistModal';
import type { Appointment } from '@/types/models';

/** Mesma chave da QueuePage / PatientQuickPanel — sala persiste por usuário. */
const ROOM_STORAGE_KEY = (uid: string) => `dratento.queue.room.${uid}`;

/** Dilatação mínima antes de mandar entrar — mesmo valor do PatientQuickPanel. */
const MIN_DILATION_MS = 15 * 60 * 1000;

/**
 * "Avisar médico" da SECRETÁRIA num clique: manda o pop-up "próximo paciente"
 * pra tela do médico do agendamento ([[NextPatientWatcher]]) dizendo QUAL
 * paciente está entrando. Usado nos cartões "próximo" da gaveta da agenda
 * ([[DayAgendaDrawer]]) e da sidebar da Agenda ([[AgendaSidebar]]).
 *
 * Mesmas travas do "Avisar médico" do [[PatientQuickPanel]]: sem médico não
 * envia; dilatação recente pede confirmação; convênio do dia precisa estar
 * definido; acompanhante precisa ter sido perguntado — abre o alerta e
 * conclui o envio depois da resposta. O consumidor renderiza `modal`.
 */
export function useNotifyDoctor(
  clinicId: string | null | undefined,
  opts?: { onError?: (msg: string) => void },
): {
  notify: (a: Appointment) => Promise<void>;
  sendingId: string | null;
  sentId: string | null;
  modal: ReactNode;
} {
  const { user } = useAuth();
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentId, setSentId] = useState<string | null>(null);
  // Agendamento cujo aviso espera a resposta "entra acompanhado?".
  const [askFor, setAskFor] = useState<Appointment | null>(null);
  const onErrorRef = useRef(opts?.onError);
  onErrorRef.current = opts?.onError;
  const report = (msg: string) => (onErrorRef.current ?? ((m) => window.alert(m)))(msg);

  const send = useCallback(async (a: Appointment) => {
    if (!clinicId || !user || !a.professionalId) return;
    setSendingId(a.id);
    try {
      // Acompanhante FRESCO: o alerta pode ter acabado de gravá-lo e o
      // snapshot da lista ainda estar defasado.
      let accompanistName = a.accompanistName;
      try {
        const fresh = await AppointmentService.getAppointment(clinicId, a.id);
        if (fresh) accompanistName = fresh.accompanistName;
      } catch {
        // Sem acesso ao doc agora — segue com o snapshot.
      }
      await DoctorAlertService.sendNextPatient(clinicId, {
        patientName: a.patientName || 'Paciente',
        patientId: a.patientId,
        accompanistName,
        room: localStorage.getItem(ROOM_STORAGE_KEY(user.uid))?.trim() || undefined,
        appointmentId: a.id,
        professionalId: a.professionalId,
        sentByUid: user.uid,
        sentByName: user.displayName || user.email || undefined,
      });
      setSentId(a.id);
    } catch (err) {
      console.error('[notify-doctor] avisar médico falhou:', err);
      report('Não foi possível avisar o médico. Tente de novo.');
    } finally {
      setSendingId(null);
    }
  }, [clinicId, user]);

  const notify = useCallback(async (a: Appointment) => {
    if (!clinicId || !user || sendingId) return;
    const name = a.patientName || 'o paciente';
    if (!a.professionalId) {
      report(`O agendamento de ${name} está sem médico definido — defina o médico antes de avisar.`);
      return;
    }
    if (a.attendancePausedForDilation && a.dilationPausedAt) {
      const elapsedMs = Date.now() - a.dilationPausedAt;
      if (elapsedMs < MIN_DILATION_MS) {
        const min = Math.floor(elapsedMs / 60000);
        const ok = window.confirm(
          `💧 ${a.patientName || 'Este paciente'} saiu para dilatar a pupila há ${min < 1 ? 'menos de 1 minuto' : `${min} min`}.\n` +
            `O ideal é aguardar pelo menos 15 minutos de dilatação.\n\n` +
            `Avisar o médico que este paciente vai entrar mesmo assim?`,
        );
        if (!ok) return;
      }
    }
    if (!a.insurerId && a.insurerChoice == null) {
      report(`Defina o convênio do dia de ${name} (abra o agendamento) antes de avisar o médico.`);
      return;
    }
    if (!a.accompanistId && !a.accompanistAsked) {
      setAskFor(a);
      return;
    }
    await send(a);
  }, [clinicId, user, sendingId, send]);

  const modal = (
    <NextUpAccompanistModal
      isOpen={askFor !== null}
      clinicId={clinicId}
      appointment={askFor}
      onDecided={() => {
        if (askFor) void send(askFor);
      }}
      onClose={() => setAskFor(null)}
    />
  );

  return { notify, sendingId, sentId, modal };
}
